import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import API_CONFIG from '../../../config/api';

/** Align with backend IngestionJob.status; tolerate legacy/alternate spellings. */
const isJobSucceeded = (status) => {
  const s = String(status || '').toLowerCase();
  return s === 'succeeded' || s === 'completed' || s === 'success';
};

const isJobFailed = (status) => {
  const s = String(status || '').toLowerCase();
  return s === 'failed' || s === 'error';
};

const isJobCancelled = (status) => {
  const s = String(status || '').toLowerCase();
  return s === 'cancelled' || s === 'canceled';
};

const JOB_POLL_INTERVAL_MS = 2500;
const JOB_POLL_MAX_TICKS = 144; // ~6 minutes

const useProductKnowledgeReimport = ({
  selectedProject,
  setSnackbar,
  refreshPrimaryLists,
}) => {
  const [reimportLoadingModal, setReimportLoadingModal] = useState(false);
  const [reimportProgress, setReimportProgress] = useState(0);
  const [reimportMessage, setReimportMessage] = useState('Preparing re-import...');
  const [reimportDetailLine, setReimportDetailLine] = useState('');
  const [figmaReimportDialogOpen, setFigmaReimportDialogOpen] = useState(false);
  const [figmaReimportPlan, setFigmaReimportPlan] = useState(null);
  const [figmaReimportItem, setFigmaReimportItem] = useState(null);

  const reimportProgressTimerRef = useRef(null);
  const reimportMessageTimerRef = useRef(null);
  const figmaJobPollRef = useRef(null);
  const figmaJobPollTicksRef = useRef(0);
  const figmaJobIdRef = useRef(null);
  const jiraReimportInFlightRef = useRef(false);

  const projectId = selectedProject?.project_id;

  const wittyReimportMessages = useMemo(() => ([
    'Connecting to JIRA... brewing coffee ☕',
    'Extracting Figma URLs like a detective 🕵️',
    'Syncing frames and downloading screenshots 📸',
    'Running semantic understanding... AI is thinking 🧠',
    'Classifying components and inferring flows 🔄',
    'Extracting business rules from designs 📋',
    'Indexing everything into the knowledge base 📚',
    'Almost there... polishing the details ✨',
    'Finalizing re-import... almost done! 🎉',
  ]), []);

  const wittyFigmaReimportMessages = useMemo(() => ([
    'Connecting to Figma... 🔗',
    'Fetching latest design state... 🎨',
    'Syncing frames and screenshots... 📸',
    'Updating semantic understanding... 🧠',
    'Rebuilding product knowledge... 📚',
    'Finalizing update... almost done! ✨',
  ]), []);

  const clearReimportTimers = useCallback(() => {
    if (reimportProgressTimerRef.current) {
      clearInterval(reimportProgressTimerRef.current);
      reimportProgressTimerRef.current = null;
    }
    if (reimportMessageTimerRef.current) {
      clearInterval(reimportMessageTimerRef.current);
      reimportMessageTimerRef.current = null;
    }
    if (figmaJobPollRef.current) {
      clearInterval(figmaJobPollRef.current);
      figmaJobPollRef.current = null;
    }
    figmaJobPollTicksRef.current = 0;
  }, []);

  const closeFigmaReimportDialog = useCallback(() => {
    setFigmaReimportDialogOpen(false);
    setFigmaReimportPlan(null);
    setFigmaReimportItem(null);
  }, []);

  /** Close plan UI only; keep figmaReimportItem for execute + snackbar context. */
  const closeFigmaPlanOnly = useCallback(() => {
    setFigmaReimportDialogOpen(false);
    setFigmaReimportPlan(null);
  }, []);

  const closeReimportLoadingModal = useCallback(() => {
    setReimportLoadingModal(false);
    clearReimportTimers();
    const jobId = figmaJobIdRef.current;
    const jira = jiraReimportInFlightRef.current;
    figmaJobIdRef.current = null;
    jiraReimportInFlightRef.current = false;
    if (jobId) {
      setSnackbar({
        open: true,
        message: 'Figma re-import is still running in the background. Refresh in a few minutes.',
        severity: 'info',
      });
    } else if (jira) {
      setSnackbar({
        open: true,
        message: 'JIRA re-import may still be running on the server. Refresh Product Knowledge in a minute.',
        severity: 'info',
      });
    }
  }, [clearReimportTimers, setSnackbar]);

  const runJiraReimport = useCallback(async (item) => {
    if (!item || !projectId) return;

    const finishJira = async (ok, key, title, errorMessage) => {
      clearReimportTimers();
      jiraReimportInFlightRef.current = false;
      setReimportProgress(ok ? 100 : 0);
      setReimportMessage(ok ? 'Done' : 'Re-import failed');
      setReimportDetailLine('');
      setReimportLoadingModal(false);
      if (ok) {
        try {
          await refreshPrimaryLists();
        } catch {
          // refresh is best-effort; user can use Refresh
        }
        setSnackbar({
          open: true,
          message: `Re-import completed (JIRA import finished). Updated ${key} — "${title}"`,
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: errorMessage || 'JIRA re-import failed',
          severity: 'error',
        });
      }
    };

    try {
      const meta = item?.content?.metadata || {};
      const jiraId = meta.id;
      let key = null;
      if (jiraId && String(jiraId).startsWith('JIRA:')) {
        key = String(jiraId).split(':')[1];
      }
      if (!key && item?.content?.external_key) {
        key = String(item.content.external_key).trim();
      }
      if (!key && item?.knowledge_id && String(item.knowledge_id).startsWith('JIRA_')) {
        key = String(item.knowledge_id).slice(5).replace(/_/g, '-');
      }
      if (!key) {
        setSnackbar({
          open: true,
          message: 'Could not resolve JIRA issue key for re-import. Re-import from JIRA import with the issue key.',
          severity: 'warning',
        });
        return;
      }

      setReimportLoadingModal(true);
      setReimportProgress(0);
      setReimportMessage(wittyReimportMessages[0]);
      setReimportDetailLine('Waiting for JIRA import to finish (HTTP response)…');
      jiraReimportInFlightRef.current = true;
      clearReimportTimers();

      let progress = 0;
      let messageIndex = 0;
      reimportProgressTimerRef.current = setInterval(() => {
        progress += Math.random() * 15;
        if (progress > 90) progress = 90;
        setReimportProgress(progress);
      }, 500);

      reimportMessageTimerRef.current = setInterval(() => {
        messageIndex = (messageIndex + 1) % wittyReimportMessages.length;
        setReimportMessage(wittyReimportMessages[messageIndex]);
      }, 6000);

      const title = item?.content?.name || item?.content?.title || 'PRD';
      const res = await API_CONFIG.request('/jira/import', {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId, input: key }),
      });
      let json = null;
      try {
        json = await res.json();
      } catch {
        json = null;
      }

      if (!res.ok) {
        const detail = json?.detail || json?.message || `HTTP ${res.status}`;
        await finishJira(false, key, title, detail);
        return;
      }

      if (!json?.success) {
        await finishJira(false, key, title, json?.detail || json?.message || 'Re-import failed');
        return;
      }

      await finishJira(true, key, title, null);
    } catch (error) {
      await finishJira(
        false,
        '',
        '',
        error?.message || 'Re-import failed',
      );
    }
  }, [clearReimportTimers, projectId, refreshPrimaryLists, setSnackbar, wittyReimportMessages]);

  const runFigmaReimportExecute = useCallback(async (action) => {
    const plan = figmaReimportPlan;
    const item = figmaReimportItem;
    if (!plan?.figma_url || !projectId) return;

    closeFigmaPlanOnly();
    setReimportLoadingModal(true);
    setReimportProgress(0);
    setReimportMessage(wittyFigmaReimportMessages[0]);
    setReimportDetailLine('Submitting Figma re-import…');
    figmaJobIdRef.current = null;
    clearReimportTimers();

    let progress = 0;
    let messageIndex = 0;
    reimportProgressTimerRef.current = setInterval(() => {
      progress += Math.random() * 12;
      if (progress > 90) progress = 90;
      setReimportProgress(progress);
    }, 500);

    reimportMessageTimerRef.current = setInterval(() => {
      messageIndex = (messageIndex + 1) % wittyFigmaReimportMessages.length;
      setReimportMessage(wittyFigmaReimportMessages[messageIndex]);
    }, 5000);

    const entryTitle =
      item?.content?.name || item?.content?.title || item?.title || 'Figma Design';

    const finishFigmaFailure = (msg) => {
      clearReimportTimers();
      figmaJobIdRef.current = null;
      setReimportDetailLine('');
      setReimportLoadingModal(false);
      setFigmaReimportItem(null);
      setSnackbar({ open: true, message: msg || 'Figma re-import failed', severity: 'error' });
    };

    const finishFigmaSuccess = async (modeLabel) => {
      clearReimportTimers();
      figmaJobIdRef.current = null;
      setReimportProgress(100);
      setReimportMessage('Done');
      setReimportDetailLine('');
      setReimportLoadingModal(false);
      setFigmaReimportItem(null);
      try {
        await refreshPrimaryLists();
      } catch {
        // best-effort
      }
      setSnackbar({
        open: true,
        message: `Re-import completed (${modeLabel}). Updated Figma — "${entryTitle}" (Staged)`,
        severity: 'success',
      });
    };

    try {
      const res = await API_CONFIG.request('/api/figma/ingest-direct/execute', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          figma_url: plan.figma_url,
          title: plan.title,
          action,
        }),
      });
      let json = null;
      try {
        json = await res.json();
      } catch {
        json = null;
      }
      if (!res.ok || !json?.success) {
        finishFigmaFailure(json?.detail || json?.message || `Figma re-import failed (HTTP ${res.status})`);
        return;
      }

      const jobId = json?.result?.job_id;
      if (jobId) {
        figmaJobIdRef.current = jobId;
        if (reimportProgressTimerRef.current) clearInterval(reimportProgressTimerRef.current);
        if (reimportMessageTimerRef.current) clearInterval(reimportMessageTimerRef.current);
        reimportProgressTimerRef.current = null;
        reimportMessageTimerRef.current = null;

        setReimportMessage(json?.result?.message || 'Queued…');
        setReimportProgress(Number(json?.result?.progress) || 0);
        setReimportDetailLine('Tracking background job until it reaches a terminal status…');
        figmaJobPollTicksRef.current = 0;

        figmaJobPollRef.current = setInterval(async () => {
          figmaJobPollTicksRef.current += 1;
          if (figmaJobPollTicksRef.current > JOB_POLL_MAX_TICKS) {
            clearReimportTimers();
            figmaJobIdRef.current = null;
            setFigmaReimportItem(null);
            setReimportLoadingModal(false);
            setReimportDetailLine('');
            setSnackbar({
              open: true,
              message:
                'Figma re-import status polling timed out. The job may still complete — refresh Product Knowledge shortly.',
              severity: 'warning',
            });
            return;
          }
          try {
            const response = await API_CONFIG.request(`/api/figma/jobs/${encodeURIComponent(jobId)}`, { method: 'GET' });
            const payload = await response.json();
            if (!response.ok) return;

            const status = payload?.status;
            const progressValue = Number(payload?.progress ?? 0);
            const message = payload?.message || (status === 'running' ? 'Running…' : 'Queued…');
            setReimportProgress(Number.isFinite(progressValue) ? progressValue : 0);
            setReimportMessage(message);

            if (isJobSucceeded(status)) {
              if (figmaJobPollRef.current) clearInterval(figmaJobPollRef.current);
              figmaJobPollRef.current = null;
              await finishFigmaSuccess('background job finished');
              return;
            }

            if (isJobFailed(status) || isJobCancelled(status)) {
              if (figmaJobPollRef.current) clearInterval(figmaJobPollRef.current);
              figmaJobPollRef.current = null;
              const err = payload?.error || payload?.message || 'Figma re-import failed';
              finishFigmaFailure(
                isJobCancelled(status) ? `Figma re-import cancelled: ${err}` : `Figma re-import failed (background job): ${err}`,
              );
            }
          } catch {
            // Keep polling until timeout.
          }
        }, JOB_POLL_INTERVAL_MS);
        return;
      }

      await finishFigmaSuccess('immediate server response');
    } catch (error) {
      finishFigmaFailure(error?.message || 'Figma re-import request failed');
    }
  }, [
    clearReimportTimers,
    closeFigmaPlanOnly,
    figmaReimportItem,
    figmaReimportPlan,
    projectId,
    refreshPrimaryLists,
    setSnackbar,
    wittyFigmaReimportMessages,
  ]);

  useEffect(() => {
    const onReimport = async (event) => {
      const { item } = event.detail || {};
      await runJiraReimport(item);
    };

    const onFigmaReimport = async (event) => {
      const { item } = event.detail || {};
      if (!item || !projectId) return;
      try {
        const figmaUrl = item?.content?.figma?.ingested_url;
        if (!figmaUrl) {
          setSnackbar({ open: true, message: 'Figma URL not found for this entry.', severity: 'error' });
          return;
        }

        const title = (item?.content?.name || item?.content?.title || item?.title || '').trim() || 'Figma Design';
        const res = await API_CONFIG.request('/api/figma/ingest-direct/plan', {
          method: 'POST',
          body: JSON.stringify({
            project_id: projectId,
            figma_url: figmaUrl,
            title,
          }),
        });
        const json = await res.json();
        if (!res.ok || !json?.success) {
          throw new Error(json?.detail || json?.message || 'Failed to plan Figma re-import');
        }

        setFigmaReimportItem(item);
        setFigmaReimportPlan({ ...json.plan, figma_url: figmaUrl, title });
        setFigmaReimportDialogOpen(true);
      } catch (error) {
        setSnackbar({ open: true, message: error.message || 'Failed to plan Figma re-import', severity: 'error' });
      }
    };

    window.addEventListener('pkb-reimport-request', onReimport);
    window.addEventListener('pkb-figma-reimport-request', onFigmaReimport);

    return () => {
      window.removeEventListener('pkb-reimport-request', onReimport);
      window.removeEventListener('pkb-figma-reimport-request', onFigmaReimport);
      clearReimportTimers();
    };
  }, [clearReimportTimers, projectId, runJiraReimport, setSnackbar]);

  return {
    reimportLoadingModal,
    reimportProgress,
    reimportMessage,
    reimportDetailLine,
    closeReimportLoadingModal,
    figmaReimportDialogOpen,
    figmaReimportPlan,
    closeFigmaReimportDialog,
    runFigmaReimportExecute,
  };
};

export default useProductKnowledgeReimport;
